export type PairJudgment = 'good' | 'weak' | 'unrelated' | 'unsure';

export const PAIR_JUDGMENTS = ['good', 'weak', 'unrelated', 'unsure'] as const;

export const PAIR_JUDGMENT_LABELS: Record<PairJudgment, string> = {
  good: 'Good pair',
  weak: 'Weakly related',
  unrelated: 'Unrelated',
  unsure: 'Unsure',
};

const TITLE_STOPWORDS = new Set([
  'analysis',
  'approach',
  'attacks',
  'based',
  'benchmarking',
  'characterizing',
  'detection',
  'efficient',
  'exploring',
  'framework',
  'learning',
  'measuring',
  'model',
  'models',
  'practical',
  'precise',
  'sound',
  'study',
  'system',
  'systems',
  'toward',
  'towards',
  'understanding',
  'using',
  'via',
  'with',
]);

const AUTHOR_STOPWORDS = new Set([
  'and',
  'the',
  'university',
  'institute',
  'laboratory',
  'college',
  'department',
  'school',
  'research',
  'center',
  'centre',
]);

export interface CorpusPaper {
  id: string;
  title: string;
  abstract: string;
  authors: string;
  conference: string;
  year: number;
  session: string;
  topic: string;
  sourceUrl: string;
  doi: string;
}

export interface SessionPair {
  id: string;
  expertiseId: string;
  preferenceId: string;
  conference: string;
  year: number;
  session: string;
  topic: string;
  relatedness: number;
  sameSessionIds: string[];
  hardNegativeIds: string[];
  sessionTight: boolean;
}

export interface PairReview {
  judgment: PairJudgment;
  note: string;
  updatedAt: string;
}

export interface PairReviewStore {
  version: 1;
  reviews: Record<string, PairReview>;
}

export type QualityFilter = 'all' | 'noisy' | 'clones';
export type PairSort = 'attention' | 'relatedness-asc' | 'relatedness-desc' | 'fixture';
export type QualityFlag =
  | 'mixed-session'
  | 'low-overlap'
  | 'title-clone'
  | 'topic-mismatch'
  | 'shared-author';

export interface PairFilter {
  conference: string;
  year: string;
  topic: string;
  tightness: 'all' | 'tight' | 'loose';
  judgment: 'all' | 'unreviewed' | PairJudgment;
  quality: QualityFilter;
  query: string;
}

export interface PairAssessment {
  titleOverlap: number;
  abstractOverlap: number;
  relatedness: number;
  sharedTitleTokens: string[];
  flags: QualityFlag[];
  keepReason: string;
}

export interface ReviewCounts {
  good: number;
  weak: number;
  unrelated: number;
  unsure: number;
  unreviewed: number;
  total: number;
}

export function isPairJudgment(value: string): value is PairJudgment {
  return (PAIR_JUDGMENTS as readonly string[]).includes(value);
}

export function emptyReviewStore(): PairReviewStore {
  return { version: 1, reviews: {} };
}

export function parseReviewStore(raw: string): PairReviewStore {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object') return emptyReviewStore();
  const record = parsed as { version?: unknown; reviews?: unknown };
  if (record.version !== 1 || !record.reviews || typeof record.reviews !== 'object') {
    return emptyReviewStore();
  }
  const reviews: Record<string, PairReview> = {};
  for (const [pairId, value] of Object.entries(record.reviews)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as { judgment?: unknown; note?: unknown; updatedAt?: unknown };
    if (typeof entry.judgment !== 'string' || !isPairJudgment(entry.judgment)) continue;
    reviews[pairId] = {
      judgment: entry.judgment,
      note: typeof entry.note === 'string' ? entry.note : '',
      updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : '',
    };
  }
  return { version: 1, reviews };
}

export function contentTokens(title: string): string[] {
  const tokens = title.toLowerCase().match(/[a-z][a-z0-9+-]{3,}/g) ?? [];
  return [...new Set(tokens.filter((token) => !TITLE_STOPWORDS.has(token)))];
}

export function overlappingTokens(left: string, right: string): string[] {
  const rightSet = new Set(contentTokens(right));
  return contentTokens(left).filter((token) => rightSet.has(token));
}

export function jaccard(left: readonly string[], right: readonly string[]): number {
  const rightSet = new Set(right);
  if (left.length === 0 && right.length === 0) return 0;
  const intersection = left.filter((token) => rightSet.has(token)).length;
  return intersection / (left.length + right.length - intersection);
}

export function isMixedSession(session: string): boolean {
  return /\band\b/i.test(session);
}

export function assessPair(
  pair: SessionPair,
  expertise: CorpusPaper,
  preference: CorpusPaper,
): PairAssessment {
  const sharedTitleTokens = overlappingTokens(expertise.title, preference.title);
  const titleOverlap = jaccard(contentTokens(expertise.title), contentTokens(preference.title));
  const abstractOverlap = jaccard(
    contentTokens(expertise.abstract.slice(0, 600)),
    contentTokens(preference.abstract.slice(0, 600)),
  );
  const distinctive = sharedTitleTokens.filter((token) => token.length >= 6).length;
  const sessionTokens = new Set(contentTokens(pair.session));
  const onTopic =
    Number(contentTokens(expertise.title).some((token) => sessionTokens.has(token))) +
    Number(contentTokens(preference.title).some((token) => sessionTokens.has(token)));
  const relatedness =
    pair.relatedness ||
    0.7 * titleOverlap + 0.3 * abstractOverlap + 0.05 * Math.min(distinctive, 5) + 0.02 * onTopic;
  const flags: QualityFlag[] = [];
  if (isMixedSession(pair.session)) flags.push('mixed-session');
  if (titleOverlap < 0.08 && sharedTitleTokens.length < 2) flags.push('low-overlap');
  if (titleOverlap >= 0.4) flags.push('title-clone');
  if (expertise.topic !== preference.topic) flags.push('topic-mismatch');
  if (sharedAuthorKeys(expertise.authors, preference.authors).length > 0) flags.push('shared-author');
  const keepReason =
    pair.relatedness > 0
      ? `Kept because relatedness ${pair.relatedness.toFixed(2)} sits in the mid band (0.06–0.35), not a title clone and not an unrelated mixed-session pair.`
      : pair.sessionTight
        ? 'Kept because the session name matched a tight theme.'
        : sharedTitleTokens.length >= 2
          ? `Kept because titles share: ${sharedTitleTokens.join(', ')}.`
          : 'Kept as a same-session pair without a tight theme or shared title tokens.';
  return {
    titleOverlap,
    abstractOverlap,
    relatedness,
    sharedTitleTokens,
    flags,
    keepReason,
  };
}

export function formatOverlap(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function authorKeys(authors: string): string[] {
  const cleaned = authors.replace(/\([^)]*\)/g, ' ').replace(/<[^>]+>/g, ' ');
  const keys = new Set<string>();
  for (const part of cleaned.split(/;|,|\band\b|&/)) {
    const tokens = part
      .match(/[A-Za-z][A-Za-z'-]+/g)
      ?.map((token) => token.toLowerCase())
      .filter((token) => !AUTHOR_STOPWORDS.has(token) && token.length > 1);
    const last = tokens?.at(-1);
    if (last) keys.add(last);
  }
  return [...keys];
}

export function sharedAuthorKeys(left: string, right: string): string[] {
  const rightSet = new Set(authorKeys(right));
  return authorKeys(left).filter((key) => rightSet.has(key));
}

export function filterPairs(
  pairs: readonly SessionPair[],
  papers: Map<string, CorpusPaper>,
  reviews: Record<string, PairReview>,
  filter: PairFilter,
): SessionPair[] {
  const query = filter.query.trim().toLowerCase();
  return pairs.filter((pair) => {
    if (filter.conference && pair.conference !== filter.conference) return false;
    if (filter.year && String(pair.year) !== filter.year) return false;
    if (filter.topic && pair.topic !== filter.topic) return false;
    if (filter.tightness === 'tight' && !pair.sessionTight) return false;
    if (filter.tightness === 'loose' && pair.sessionTight) return false;
    const review = reviews[pair.id];
    if (filter.judgment === 'unreviewed' && review) return false;
    if (
      filter.judgment !== 'all' &&
      filter.judgment !== 'unreviewed' &&
      review?.judgment !== filter.judgment
    ) {
      return false;
    }
    if (filter.quality !== 'all') {
      const expertise = papers.get(pair.expertiseId);
      const preference = papers.get(pair.preferenceId);
      if (!expertise || !preference) return false;
      const flags = new Set(assessPair(pair, expertise, preference).flags);
      if (filter.quality === 'noisy' && !flags.has('mixed-session') && !flags.has('low-overlap')) {
        return false;
      }
      if (filter.quality === 'clones' && !flags.has('title-clone')) return false;
    }
    if (!query) return true;
    const expertise = papers.get(pair.expertiseId);
    const preference = papers.get(pair.preferenceId);
    const haystack = [
      pair.id,
      pair.session,
      pair.topic,
      expertise?.title,
      preference?.title,
      expertise?.authors,
      preference?.authors,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function reviewCounts(
  pairs: readonly SessionPair[],
  reviews: Record<string, PairReview>,
): ReviewCounts {
  const counts: ReviewCounts = {
    good: 0,
    weak: 0,
    unrelated: 0,
    unsure: 0,
    unreviewed: 0,
    total: pairs.length,
  };
  for (const pair of pairs) {
    const judgment = reviews[pair.id]?.judgment;
    if (!judgment) counts.unreviewed += 1;
    else counts[judgment] += 1;
  }
  return counts;
}

export function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}

export function sortPairs(
  pairs: readonly SessionPair[],
  papers: Map<string, CorpusPaper>,
  reviews: Record<string, PairReview>,
  sort: PairSort,
): SessionPair[] {
  const scored = pairs.map((pair) => {
    const expertise = papers.get(pair.expertiseId);
    const preference = papers.get(pair.preferenceId);
    const relatedness =
      pair.relatedness ||
      (expertise && preference ? assessPair(pair, expertise, preference).relatedness : 0);
    return { pair, relatedness, reviewed: Boolean(reviews[pair.id]) };
  });
  if (sort === 'fixture') return scored.map((item) => item.pair);
  scored.sort((left, right) => {
    if (sort === 'attention') {
      if (left.reviewed !== right.reviewed) return Number(left.reviewed) - Number(right.reviewed);
      return left.relatedness - right.relatedness;
    }
    if (sort === 'relatedness-asc') return left.relatedness - right.relatedness;
    return right.relatedness - left.relatedness;
  });
  return scored.map((item) => item.pair);
}

export function scholarUrl(title: string): string {
  return `https://scholar.google.com/scholar?q=${encodeURIComponent(title)}`;
}

export function dblpUrl(title: string): string {
  return `https://dblp.org/search?q=${encodeURIComponent(title)}`;
}

export function doiUrl(doi: string): string {
  return `https://doi.org/${doi}`;
}

export function paperSnippet(abstract: string, limit = 420): { text: string; truncated: boolean } {
  const compact = abstract.replace(/\s+/g, ' ').trim();
  if (compact.length <= limit) return { text: compact, truncated: false };
  return { text: `${compact.slice(0, limit).trimEnd()}…`, truncated: true };
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
